import React, { FC, useState, type JSX } from 'react';
import {Button, Collapse, Menu, MenuItem, TableCell, TableRow, Typography} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {IActionItem} from '../Table/Table';
import {styled} from "@mui/material/styles";

const TableRowLabelTitle = styled(Typography)(() => ({
  marginLeft: '0.8em'
}));

const TableRowMenuContainer = styled(Menu)(() => ({
  '& .MuiPopover-paper': {
    boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
  }
}));

const TableRowTableCell = styled(TableCell)(() => ({
  fontSize: '0.8em',
  fontWeight: '500'
}));

/**
 * @interface ITableRow
 * @property {{[key: string]: any}} [row] - The data for the row.
 * @property {boolean} [hasRowAction] - Whether the row has actions.
 * @property {Array<IActionItem>} [actionItems] - The action items for the row.
 * @property {Array<IActionItem>} [conditionalActionItems] - Conditional action items based on row index.
 * @property {string} [className] - Additional class names for styling.
 * @property {number} [inx] - The index of the row.
 * @property {boolean} [hasRowSubComponent] - Whether the row has a sub-component.
 * @property {function} [RowSubComponent] - The sub-component to render for the row.
 * @property {Array<{ id: string }>} columnOrder - **Ordered** head cell ids used to specify the order of data in the row. Useful when the row data is not in the same order as the head cells.
 * @property {"list" | "button"} actionType - The type of action to render (list or button).
 * @property {Array<{ id: any, component: JSX.Element }>} [rowMessageComponents] - Components to render as messages in the row.
 * @property {function} [actionButtons] - Function to render action buttons for the row.
 * @description This interface defines the structure of the props for the AppTableRow component.
 * It includes properties for the row data, actions, styling, and sub-components.
 * @deprecated
 */
interface ITableRow {
  row?: { [key: string]: any },
  hasRowAction?: boolean,
  actionItems?: IActionItem[],
  conditionalActionItems?: Array<IActionItem>;
  className?: any | undefined,
  inx?: number,
  hasRowSubComponent?: boolean,
  RowSubComponent?: (props: { id: any, item: any }) => JSX.Element,
  columnOrder: Array<string> | undefined,
  actionType: "list" | "button",
  rowMessageComponents?: Array<{ id: any, component: JSX.Element }>,
  actionButtons?: (props: { id: any, item: any }) => JSX.Element,
}

/**
 * @see ITableRow
 * @constructor
 * @deprecated
 */
const AppTableRow: FC<ITableRow> = ({
                       row = {},
                       hasRowAction = false,
                       actionItems,
                       conditionalActionItems,
                       className = '',
                       inx,
                       hasRowSubComponent,
                       columnOrder,
                       actionType,
                       actionButtons,
                       rowMessageComponents,
                       RowSubComponent,
                     }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>();
  const [open, setOpen] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    if (event.currentTarget instanceof HTMLButtonElement) {
      setMenuAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setMenuAnchorEl(null);
  }

  return (
    <>
      <TableRow
        style={{cursor: hasRowSubComponent ? 'pointer' : 'default'}}
        onClick={() => {
          handleClose();
          if (hasRowSubComponent) setOpen(!open)
        }}
        hover
      >
        {
          columnOrder ? columnOrder.map((key) =>
          <TableRowTableCell className={className[key]} key={key}>
            {row[key] ?? "-"}
          </TableRowTableCell>)
            :
            row.map((key: any) =>
              <TableRowTableCell className={className[key]} key={key}>
                {row[key] ?? "-"}
              </TableRowTableCell>
            )
        }
        {hasRowAction && actionType === "list" && Array.isArray(actionItems) && actionItems?.length > 0 && (
          <TableRowTableCell>
            <Button aria-controls="simple-menu" aria-haspopup="true" onClick={(e) => handleOpen(e)}>
              <MoreHorizIcon fontSize='medium'/>
            </Button>
            <TableRowMenuContainer
              id="simple-menu"
              anchorEl={menuAnchorEl}
              keepMounted
              open={Boolean(menuAnchorEl)}
              onClose={handleClose}
            >
              {actionItems?.map((item, index) => (
                <MenuItem key={index} onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                  item.onClick(row)
                }}>
                  {item.icon}
                  <TableRowLabelTitle variant='body1' color='primary'>
                    {item.label}
                  </TableRowLabelTitle>
                </MenuItem>
              ))}
              {conditionalActionItems &&
                conditionalActionItems.find(item => item.id === inx) && (
                  <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    conditionalActionItems.find(item => item.id === inx)?.onClick(row)
                  }}>
                    {conditionalActionItems.find(item => item.id === inx)?.icon}
                    <TableRowLabelTitle variant='body1' color='primary'>
                      {conditionalActionItems.find(item => item.id === inx)?.label}
                    </TableRowLabelTitle>
                  </MenuItem>
                )
              }
            </TableRowMenuContainer>
          </TableRowTableCell>
        )}

        {hasRowAction && actionType === "button" && actionButtons && (
          <TableRowTableCell component="th">
            {actionButtons({id: inx, item: row})}
          </TableRowTableCell>
        )}
      </TableRow>

      {rowMessageComponents && rowMessageComponents.length > 0 && rowMessageComponents.find((item) => item.id === inx) &&
        <TableRow>
          <TableCell style={{paddingBottom: 0, paddingTop: 0}}
                     colSpan={hasRowAction ? Object.keys(row).length + 1 : Object.keys(row).length}>
            {rowMessageComponents.find((item) => item.id === inx)?.component}
          </TableCell>
        </TableRow>
      }

      {RowSubComponent &&
        <TableRow>
          <TableCell style={{paddingBottom: 0, paddingTop: 0}}
                     colSpan={hasRowAction ? Object.keys(row).length + 1 : Object.keys(row).length}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <RowSubComponent id={inx} item={row}/>
            </Collapse>
          </TableCell>
        </TableRow>
      }
    </>
  )
}


export default AppTableRow
